import cn from 'classnames';
import StudioSelectionSearchBar from 'components/Common/StudioSelectionSearchBar/StudioSelectionSearchBar';
import React, { FC } from 'react';
import { FontWeight, Theme } from 'store/landing/types';
import { useLandingPageContext } from 'context/LandingPageContext';
import { useSelector } from 'react-redux';
import getLocaleSelector from 'store/app/selector/getLocaleSelector';
import getLandingPageName from 'store/landing/selector/getLandingPageName';
import gtmSendEvent from 'utils/gtm/gtmSendEvent';
import gtmLandingCTAEvent from 'utils/gtm/gtmLandingCTAEvent';
import AppleLogo from 'assets/images/appstore_updated.png';
import GoogleLogo from 'assets/images/google-play_updated.png';
import getConfigurationByKey from 'store/app/selector/getConfigurationByKey';
import getLandingPageTheme from 'store/landing/selector/getLandingPageTheme';
import styles from './BigBannerGroup.module.scss';
import LPButton, { LPButtonSize } from '../LPButton/LPButton';

export interface BigBannerGroupProps {
  entryId: string;
  height: number;
  heightMobile: number;
  h1: string;
  h1Color: string;
  h1ColorPhone: string;
  h1FontWeight: FontWeight;
  h2: string;
  h2Color: string;
  h2ColorPhone: string;
  h2FontWeight?: FontWeight;
  renderH1Desktop: boolean;
  renderH1Mobile: boolean;
  renderH2Desktop: boolean;
  renderH2Mobile: boolean;
  buttonLabel: string;
  buttonColor: string;
  buttonBackgroundColor: string;
  buttonBorderColor: string;
  referenceUrl: string;
  bannerImageUrl: string;
  bannerImageUrlDesktop: string;
  bannerImageUrlTablet: string;
  bannerImageUrlMobile: string;
  theme?: Theme;
  features: string[];
  headingTag?: 'h1' | 'h2';
  appDownloadButtons: boolean;
}

const BigBannerGroup: FC<BigBannerGroupProps> = ({
  entryId,
  height: heightDesktop,
  heightMobile,
  h1,
  h1Color,
  h1ColorPhone,
  h1FontWeight,
  h2,
  h2ColorPhone,
  h2FontWeight,
  h2Color,
  buttonLabel,
  buttonColor,
  buttonBackgroundColor,
  buttonBorderColor,
  referenceUrl,
  bannerImageUrl,
  bannerImageUrlMobile,
  features,
  renderH1Desktop,
  renderH1Mobile,
  renderH2Desktop,
  renderH2Mobile,
  theme,
  headingTag = 'h2',
  appDownloadButtons,
}) => {
  const { isSearchBarEnabled, isDesktopDevice, categoryId, theme: globalTheme } = useLandingPageContext();
  const containerTheme = globalTheme === 'shopify' ? globalTheme : theme;
  const isSearchBarShown =
    isSearchBarEnabled && (features?.includes('studio-selection-search-bar') || entryId === '3sLDlcSNFEyiPl43YNhm7c'); // TODO: remove hardcoded value
  const backgroundImage = isDesktopDevice ? `url(${bannerImageUrl})` : `url(${bannerImageUrlMobile})`;
  const height = isDesktopDevice ? heightDesktop : heightMobile;
  const h1color = isDesktopDevice ? h1Color : h1ColorPhone;
  const h2color = isDesktopDevice ? h2Color : h2ColorPhone;
  const locale = useSelector(getLocaleSelector);
  const landingPageName = useSelector(getLandingPageName);
  const landingPageTheme = useSelector(getLandingPageTheme);
  const urls = useSelector(getConfigurationByKey('NATIVE_APP_URLS'));
  const { APPLE_URL, ANDROID_URL } = urls;

  const trackLandingPageCTA = (buttonText: string) => {
    const funnelStep = landingPageName === 'Home' ? 'homepage' : 'landing';
    gtmSendEvent(gtmLandingCTAEvent(buttonText, locale, funnelStep));
  };

  const shouldDisplayCaptionsBox = h1 || h2 || buttonLabel || appDownloadButtons;

  const renderShopifyHeading = () => {
    return <h1 className={styles.shopifyHeading}>{h1}</h1>;
  };
  const renderShopifySubheading = () => {
    return <p className={styles.shopifySubHeading}>{h2}</p>;
  };

  const renderShopifyButton = () => {
    return (
      <a href={referenceUrl} role="button" title={buttonLabel} className={styles.shopifyButton}>
        Shop all
      </a>
    );
  };

  const renderHeading = () => {
    if (globalTheme === 'shopify') {
      return renderShopifyHeading();
    }
    return (
      <>
        {isDesktopDevice &&
          renderH1Desktop &&
          React.createElement(headingTag, {
            className: styles.h1,
            dangerouslySetInnerHTML: { __html: h1 },
            style: {
              color: h1color,
              fontWeight: h1FontWeight,
            },
          })}
        {!isDesktopDevice &&
          renderH1Mobile &&
          React.createElement(headingTag, {
            className: styles.h1,
            dangerouslySetInnerHTML: { __html: h1 },
            style: {
              color: h1color,
              fontWeight: h1FontWeight,
            },
          })}
      </>
    );
  };

  const renderSubheading = () => {
    if (globalTheme === 'shopify') {
      return renderShopifySubheading();
    }
    return (
      <>
        {isDesktopDevice && renderH2Desktop && h2 && (
          <h2
            className={styles.h2}
            style={{ color: h2color, fontWeight: h2FontWeight }}
            dangerouslySetInnerHTML={{ __html: h2 }}
          />
        )}
        {!isDesktopDevice && renderH2Mobile && h2 && (
          <h2
            className={styles.h2}
            style={{ color: h2color, fontWeight: h2FontWeight }}
            dangerouslySetInnerHTML={{ __html: h2 }}
          />
        )}
      </>
    );
  };

  const renderButton = () => {
    if (!buttonLabel) {
      return null;
    }
    if (globalTheme === 'shopify') {
      return renderShopifyButton();
    }

    return (
      <>
        <LPButton
          buttonBackgroundColor={buttonBackgroundColor}
          buttonBorderColor={buttonBorderColor || buttonBackgroundColor}
          buttonColor={buttonColor}
          referenceUrl={referenceUrl}
          className={styles.button}
          size={LPButtonSize.xlg}
        >
          <span
            dangerouslySetInnerHTML={{ __html: buttonLabel }}
            className={cn(styles.buttonLabel, {
              [styles.buttonStudioLabel]: landingPageTheme === 'studio',
            })}
            onClick={() => trackLandingPageCTA(buttonLabel)}
          />
        </LPButton>
      </>
    );
  };

  return (
    <div className={cn(styles.container, styles[`theme-${containerTheme}`])}>
      <div className={styles.image} style={{ backgroundImage, height }} />
      <div className={styles.overlay}>
        {shouldDisplayCaptionsBox && (
          <div className={styles.captionsBox}>
            <div className={styles.bannerCaption}>
              {renderHeading()}
              {renderSubheading()}
              {isSearchBarShown ? (
                <StudioSelectionSearchBar categoryId={categoryId} className={styles.searchBar} />
              ) : (
                renderButton()
              )}
              {appDownloadButtons && (
                <div className={styles.appDownloadButtons}>
                  <a href={APPLE_URL} target="_blank" rel="noopener noreferrer">
                    <img src={AppleLogo} alt="Download on the App Store" className={styles.appStoreBadge} />
                  </a>
                  <a href={ANDROID_URL} target="_blank" rel="noopener noreferrer">
                    <img src={GoogleLogo} alt="Get it on Google Play" className={styles.googlePlayBadge} />
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BigBannerGroup;
